import * as React from "react";
import "hammerjs";
import {
  Chart,
  ChartArea,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartLegendItem,
  ChartSeries,
  ChartSeriesItem,
  ChartTooltip,
  ChartValueAxis,
  ChartValueAxisItem,
  DashType,
  SharedTooltipContext,
  TooltipContext,
} from "@progress/kendo-react-charts";
import {
  defaultCategoryAxisFormatter,
  defaultValueAxisFormatter,
} from "../../utils/defaultFormatters";
import {
  TimeSeriesSharedTooltipRenderer,
  TooltipSettings,
} from "../TimeSeriesSharedTooltip/TimeSeriesSharedTooltipRenderer";

export interface TimeSeriesChartItem {
  category: string;
  entries: {
    date: string | Date;
    value?: number;
    open?: number;
    high?: number;
    low?: number;
    close?: number;
  }[];
  color?: any;
  dashType?: DashType;
  lineWidth?: number;
  lineStyle?: "smooth" | "normal" | "step";
  seriesType?: string;
}

type SharedTooltipRenderType = (
  info: SharedTooltipContext,
  valueAxisFormatter?: (e?: number) => string,
  categoryAxisFormatter?: (e?: string) => string,
  settings?: TooltipSettings
) => React.ReactNode;

export interface TimeSeriesStackedChartProps {
  items: TimeSeriesChartItem[];
  baseUnitStep?: number | "auto";
  baseUnit?:
    | "auto"
    | "fit"
    | "years"
    | "months"
    | "weeks"
    | "days"
    | "hours"
    | "minutes"
    | "seconds";
  sharedTooltipRender?: SharedTooltipRenderType;
  valueAxisFormatter?(e?: number): string;
  categoryAxisFormatter?(e?: string, ev?: any): string;
  chartStyle?: React.CSSProperties;
  aggregate?: any;
  axisCrossingValue?: number;
  maxDivisions?: number;
  tooltipSettings?: TooltipSettings;
  stack?: boolean;
  height?: number;
  width?: number;
}

export const TimeSeriesStackedChart = ({
  items,
  sharedTooltipRender = TimeSeriesSharedTooltipRenderer,
  baseUnit = "auto",
  baseUnitStep = "auto",
  aggregate = "sum",
  valueAxisFormatter = defaultValueAxisFormatter,
  categoryAxisFormatter = defaultCategoryAxisFormatter,
  chartStyle,
  axisCrossingValue = 0,
  maxDivisions = 5,
  tooltipSettings,
  stack = true,
  height,
  width,
}: TimeSeriesStackedChartProps) => {
  return (
    // @ts-ignore - bad type missing children
    <Chart style={{ ...chartStyle, width, height }}>
      <ChartArea width={width} height={height} background={"transparent"} />
      <ChartLegend visible={false} />
      {typeof sharedTooltipRender === "function" && (
        <ChartTooltip
          opacity={0.9}
          color={"var(--tooltip-color)"}
          background={"var(--tooltip-background-color)"}
          render={(info: any) =>
            sharedTooltipRender(
              info as SharedTooltipContext,
              valueAxisFormatter,
              categoryAxisFormatter,
              tooltipSettings
            )
          }
          shared={true}
        />
      )}
      <ChartSeries>
        {items.map((item) => (
          <ChartSeriesItem
            key={item.category}
            aggregate={aggregate}
            categoryField="date"
            stack={stack}
            data={item.entries}
            name={item.category}
            color={item.color}
            border={{
              width: 0.1,
            }}
          />
        ))}
      </ChartSeries>
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          majorGridLines={{
            color: "transparent",
            width: 0.1,
          }}
          line={{ color: "transparent" }}
          labels={{
            color: "var(--color)",
            content: (e: any) => categoryAxisFormatter(e.value),
          }}
          maxDivisions={maxDivisions}
          baseUnit={baseUnit}
        />
      </ChartCategoryAxis>
      <ChartValueAxis>
        <ChartValueAxisItem
          majorGridLines={{
            color: "var(--muted-color)",
            width: 0.1,
          }}
          axisCrossingValue={axisCrossingValue}
          labels={{
            color: "var(--color)",
            content: (e: any) => valueAxisFormatter(e.value),
          }}
        />
      </ChartValueAxis>
    </Chart>
  );
};

export default TimeSeriesStackedChart;
